/**
 * Ravan Command Interpreter — Layer 2
 *
 * Converts natural-language user messages into structured RavanCommand objects.
 * Two modes:
 *   1. Pattern matching  — instant, zero-credit, handles common phrases
 *   2. LLM parsing      — for ambiguous or complex commands (via Edge Function)
 *
 * The interpreter never executes anything — it only produces a command that
 * the Execution Engine (Layer 5 / ravanTools.ts) will act on after user approval.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/utils/logger';
import type { EstimateContext } from '@/services/aiService';

// ── Command Types ─────────────────────────────────────────────────────────────

export type RavanAction =
  | 'add_item'
  | 'add_section'
  | 'add_room'
  | 'update_rate'
  | 'update_quantity'
  | 'generate_specs'
  | 'detect_gaps'
  | 'draft_proposal'
  | 'draft_terms'
  | 'client_summary'
  | 'suggest_rate'
  | 'create_estimate'
  | 'check_readiness'
  | 'general_question'
  | 'add_catalogue_item'
  | 'edit_catalogue_item'
  | 'delete_catalogue_item'
  | 'search_catalogue'
  | 'create_quote'
  | 'duplicate_quote'
  | 'apply_catalogue_to_quote'
  | 'set_payment_schedule'
  | 'analyze_price_trend'
  | 'benchmark_item'
  | 'summarize_quote'
  | 'draft_voucher'
  | 'post_voucher';

export interface ParsedCommand {
  action: RavanAction;
  parameters: Record<string, string | number | boolean | null>;
  confidence: 'high' | 'medium' | 'low';
  requiresApproval: boolean;  // true for all destructive/creative actions
  creditCost: number;
  rawInput: string;
  displayLabel: string;       // human-readable for the approval card
}

// Actions that always need approval before execution
const APPROVAL_REQUIRED: RavanAction[] = [
  'add_item', 'add_section', 'add_room', 'update_rate', 'update_quantity',
  'create_estimate', 'draft_proposal', 'draft_terms',
  'add_catalogue_item', 'edit_catalogue_item', 'delete_catalogue_item',
  'create_quote', 'duplicate_quote', 'draft_voucher', 'post_voucher',
];

// Credit costs per action (mirrors aiService.ts)
const ACTION_CREDIT_COST: Record<RavanAction, number> = {
  add_item:                0,
  add_section:             0,
  add_room:                0,
  update_rate:             0,
  update_quantity:         0,
  generate_specs:          1,
  suggest_rate:            1,
  client_summary:          2,
  detect_gaps:             2,
  draft_terms:             3,
  draft_proposal:          5,
  create_estimate:         5,
  check_readiness:         0,
  general_question:        0,
  add_catalogue_item:      0,
  edit_catalogue_item:     0,
  delete_catalogue_item:   0,
  search_catalogue:        0,
  create_quote:            1,
  duplicate_quote:         1,
  apply_catalogue_to_quote: 1,
  set_payment_schedule:    0,
  analyze_price_trend:     2,
  benchmark_item:          2,
  summarize_quote:         1,
  draft_voucher:           1,
  post_voucher:            1,
};

// ── Pattern rules ─────────────────────────────────────────────────────────────
// Each rule is checked in order. First match wins.

interface PatternRule {
  patterns: RegExp[];
  action: RavanAction;
  extract: (match: RegExpMatchArray, input: string) => Record<string, string | null>;
  displayLabel: (params: Record<string, string | null>) => string;
}

const PATTERN_RULES: PatternRule[] = [
  {
    patterns: [
      /add\s+(.+?)\s+(?:to|in|for)\s+(.+)/i,
      /(?:include|add)\s+(.+?)\s+in\s+(.+)/i,
    ],
    action: 'add_item',
    extract: (m) => ({ item: m[1].trim(), room: m[2].trim(), section: null, material: null }),
    displayLabel: (p) => `Add "${p.item}" to ${p.room}`,
  },
  {
    patterns: [
      /create\s+(?:a\s+)?(?:new\s+)?section\s+(?:for\s+)?(.+)/i,
      /add\s+section\s+(?:for\s+)?(.+)/i,
    ],
    action: 'add_section',
    extract: (m) => ({ name: m[1].trim(), workType: null }),
    displayLabel: (p) => `Create section "${p.name}"`,
  },
  {
    patterns: [
      /(?:generate|write|create|draft)\s+(?:specs?|specifications?)\s+(?:for\s+)?(.+)/i,
      /(?:specs?|specifications?)\s+for\s+(.+)/i,
    ],
    action: 'generate_specs',
    extract: (m) => ({ itemDescription: m[1].trim() }),
    displayLabel: (p) => `Generate specs for "${p.itemDescription}"`,
  },
  {
    patterns: [
      /(?:suggest|what(?:'s|s| is)(?: the)?)\s+(?:rate|price|cost)\s+(?:for\s+)?(.+)/i,
      /how much\s+(?:does|should|for)\s+(.+)/i,
    ],
    action: 'suggest_rate',
    extract: (m) => ({ itemDescription: m[1].trim() }),
    displayLabel: (p) => `Suggest rate for "${p.itemDescription}"`,
  },
  {
    patterns: [
      /(?:change|update|set)\s+(?:rate|price)\s+(?:of\s+)?(.+?)\s+to\s+(?:₹|rs\.?\s*)?(\d[\d,]*)/i,
    ],
    action: 'update_rate',
    extract: (m) => ({ itemDescription: m[1].trim(), newRate: m[2].replace(/,/g, '') }),
    displayLabel: (p) => `Set rate of "${p.itemDescription}" to ₹${p.newRate}`,
  },
  {
    patterns: [
      /create\s+estimate\s+(?:for\s+)?(?:a\s+)?(.+)/i,
      /(?:new|build|make)\s+estimate\s+(?:for\s+)?(.+)/i,
      /generate\s+(?:full\s+)?estimate\s+(?:for\s+)?(.+)/i,
    ],
    action: 'create_estimate',
    extract: (m) => ({ projectDescription: m[1].trim() }),
    displayLabel: (p) => `Create estimate for "${p.projectDescription}"`,
  },
  {
    patterns: [
      /(?:check|find|detect|identify|what(?:'s|s| is)?(?:\s+missing)?)\s+(?:scope\s+gaps?|missing\s+items?|gaps?)/i,
      /(?:anything|what)\s+(?:am\s+i\s+)?missing/i,
    ],
    action: 'detect_gaps',
    extract: () => ({}),
    displayLabel: () => 'Check scope gaps',
  },
  {
    patterns: [
      /(?:draft|write|generate|create)\s+(?:the\s+)?proposal/i,
      /(?:executive\s+summary|project\s+narrative)/i,
    ],
    action: 'draft_proposal',
    extract: () => ({}),
    displayLabel: () => 'Draft proposal narrative',
  },
  {
    patterns: [
      /(?:draft|write|generate)\s+(?:the\s+)?terms/i,
      /(?:terms\s+(?:and\s+conditions?|&\s+conditions?))/i,
    ],
    action: 'draft_terms',
    extract: () => ({}),
    displayLabel: () => 'Draft terms & conditions',
  },
  {
    patterns: [
      /(?:client\s+summary|whatsapp\s+message|email\s+summary|send\s+to\s+client)/i,
      /(?:short\s+summary|quick\s+message)\s+(?:for|to)\s+(?:the\s+)?client/i,
    ],
    action: 'client_summary',
    extract: () => ({}),
    displayLabel: () => 'Generate client summary',
  },
  {
    patterns: [
      /(?:is|how)\s+(?:the\s+estimate|this)\s+(?:ready|complete|done)/i,
      /(?:check|show)\s+(?:readiness|checklist|status)/i,
    ],
    action: 'check_readiness',
    extract: () => ({}),
    displayLabel: () => 'Check estimate readiness',
  },
  {
    patterns: [
      /add\s+(.+?)\s+(?:to|in)\s+(?:my\s+)?catalogue/i,
      /(?:save|store)\s+(.+?)\s+(?:in|to)\s+(?:my\s+)?catalogue/i,
      /(?:create|new)\s+catalogue\s+item\s+(?:for\s+)?(.+)/i,
    ],
    action: 'add_catalogue_item' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ name: m[1].trim(), description: '', category: 'modular' }),
    displayLabel: (p: Record<string, string | null>) => `Add "${p.name}" to catalogue`,
  },
  {
    patterns: [
      /(?:edit|update|change)\s+(?:catalogue\s+item|item)\s+(.+?)\s+(?:rate|price)\s+to\s+(?:₹|rs\.?\s*)?(\d[\d,]*)/i,
    ],
    action: 'edit_catalogue_item' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ name: m[1].trim(), default_rate: m[2].replace(/,/g, '') }),
    displayLabel: (p: Record<string, string | null>) => `Update catalogue item "${p.name}"`,
  },
  {
    patterns: [
      /(?:remove|delete)\s+(.+?)\s+from\s+(?:my\s+)?catalogue/i,
    ],
    action: 'delete_catalogue_item' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ name: m[1].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Remove "${p.name}" from catalogue`,
  },
  {
    patterns: [
      /(?:search|find|show|list)\s+(?:my\s+)?catalogue(?:\s+items?)?(?:\s+for)?\s*(.+)?/i,
    ],
    action: 'search_catalogue' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ query: m[1]?.trim() || '' }),
    displayLabel: () => 'Search catalogue',
  },
  {
    patterns: [
      /create\s+(?:a\s+)?(?:new\s+)?quote\s+for\s+(.+)/i,
      /(?:new|make|start)\s+(?:a\s+)?quote\s+for\s+(.+)/i,
      /(?:start|begin|open)\s+(?:a\s+)?new\s+estimate\s+for\s+(.+)/i,
    ],
    action: 'create_quote' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ client_name: m[1].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Create new quote for "${p.client_name}"`,
  },
  {
    patterns: [
      /(?:add|create|new)\s+(?:a\s+)?(?:room|space|area)\s+(?:called\s+|named\s+)?(.+)/i,
    ],
    action: 'add_room' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ name: m[1].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Add room "${p.name}"`,
  },
  {
    patterns: [
      /duplicate\s+(?:this\s+)?quote(?:\s+for\s+(.+))?/i,
      /clone\s+(?:this\s+)?(?:quote|estimate)(?:\s+for\s+(.+))?/i,
    ],
    action: 'duplicate_quote' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ new_client: m[1]?.trim() || '' }),
    displayLabel: () => 'Duplicate this quote',
  },
  {
    patterns: [
      /(?:price|cost|rate)\s+trend(?:s?)\s+(?:for|of)\s+(.+)/i,
      /(?:how\s+have\s+(?:prices|rates)\s+(?:changed|moved)\s+for\s+(.+))/i,
      /analyze\s+(?:pricing|price)\s+(?:for\s+)?(.+)/i,
    ],
    action: 'analyze_price_trend' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ item_query: m[1].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Analyze price trend for "${p.item_query}"`,
  },
  {
    patterns: [
      /(?:is|benchmark)\s+₹?(\d[\d,]*)\s+(?:(?:a\s+)?good\s+rate|(?:competitive|fair))\s+for\s+(.+)/i,
      /(?:how\s+does\s+)?₹?(\d[\d,]*)\s+compare\s+for\s+(.+)/i,
    ],
    action: 'benchmark_item' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ proposed_rate: m[1].replace(/,/g, ''), item_name: m[2].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Benchmark ₹${p.proposed_rate} for "${p.item_name}"`,
  },
  {
    patterns: [
      /(?:summarize|breakdown|summary)\s+(?:this|the)?\s*(?:quote|estimate)/i,
      /(?:financial|cost)\s+breakdown\s+(?:of|for)\s+(?:this\s+)?(?:quote|estimate)/i,
    ],
    action: 'summarize_quote' as RavanAction,
    extract: () => ({}),
    displayLabel: () => 'Summarize quote financials',
  },
  {
    patterns: [
      /(?:draft|create|make)\s+(?:a\s+)?(payment|receipt|journal|purchase|sales)\s+voucher\s+(?:for\s+)?(.+?)\s+for\s+(?:₹|rs\.?\s*)?(\d+(?:,\d+)*(?:\.\d+)?)/i,
    ],
    action: 'draft_voucher' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ voucher_type: m[1].toLowerCase(), party_name: m[2].trim(), amount: m[3].replace(/,/g, '') }),
    displayLabel: (p: Record<string, string | null>) => `Draft ${p.voucher_type} voucher for ₹${p.amount} (${p.party_name})`,
  },
  {
    patterns: [
      /(?:post|confirm|finalize)\s+(?:voucher\s+)?(QT-\d+|VCH-\d+|[\w-]+)/i,
    ],
    action: 'post_voucher' as RavanAction,
    extract: (m: RegExpMatchArray) => ({ voucher_number: m[1].trim() }),
    displayLabel: (p: Record<string, string | null>) => `Post voucher ${p.voucher_number}`,
  }
];

// ── Interpreter ───────────────────────────────────────────────────────────────

class CommandInterpreter {
  /**
   * Parse a user message into a structured command.
   * Tries pattern matching first (instant), falls back to LLM parsing.
   */
  async interpret(
    message: string,
    estimateContext: EstimateContext,
  ): Promise<ParsedCommand> {
    const trimmed = message.trim();

    // 1. Try pattern matching
    const patternResult = this._matchPatterns(trimmed);
    if (patternResult) {
      logger.log(`[Ravan] Pattern match: ${patternResult.action} (${patternResult.confidence})`);
      return patternResult;
    }

    // 2. Fall back to LLM interpretation (no credits — uses a cheap classify call)
    logger.log('[Ravan] No pattern match — delegating to LLM interpreter');
    return this._llmInterpret(trimmed, estimateContext);
  }

  private _matchPatterns(input: string): ParsedCommand | null {
    for (const rule of PATTERN_RULES) {
      for (const pattern of rule.patterns) {
        const match = input.match(pattern);
        if (match) {
          const params = rule.extract(match, input);
          return {
            action:           rule.action,
            parameters:       params as Record<string, string | number | boolean | null>,
            confidence:       'high',
            requiresApproval: APPROVAL_REQUIRED.includes(rule.action),
            creditCost:       ACTION_CREDIT_COST[rule.action],
            rawInput:         input,
            displayLabel:     rule.displayLabel(params),
          };
        }
      }
    }
    return null;
  }

  private async _llmInterpret(
    input: string,
    estimateContext: EstimateContext,
  ): Promise<ParsedCommand> {
    try {
      const { data, error } = await supabase.functions.invoke('ravan-interpret', {
        body: { message: input, estimateContext },
      });

      if (error || !data?.action) throw new Error(error?.message ?? 'Parse failed');

      const action: RavanAction = data.action ?? 'general_question';
      return {
        action,
        parameters:       data.parameters ?? {},
        confidence:       data.confidence ?? 'medium',
        requiresApproval: APPROVAL_REQUIRED.includes(action),
        creditCost:       ACTION_CREDIT_COST[action],
        rawInput:         input,
        displayLabel:     data.displayLabel ?? input,
      };
    } catch (err: unknown) {
      logger.warn(`[Ravan] LLM interpret failed: ${(err instanceof Error ? err.message : String(err))}`);
      // Safe fallback — treat as a general question
      return {
        action:           'general_question',
        parameters:       { question: input },
        confidence:       'low',
        requiresApproval: false,
        creditCost:       0,
        rawInput:         input,
        displayLabel:     input,
      };
    }
  }
}

export const commandInterpreter = new CommandInterpreter();
